import { CHANNEL_ID } from '@@types/common';
import { MeteringUsage } from '@@types/Metering';

import { ALERT_METERING_TYPE } from '~/constants';
import { uiState } from '~/stores/UiState';
import { action, observable } from 'mobx';
import moment from 'moment-timezone';
import { curry, mergeRight } from 'ramda';

export type HideAlert = {
  tenantAlmostOver: string | boolean;
  tenantIsOver: string | boolean;
  meAlmostOver: string | boolean;
  meIsOver: string | boolean;
};

export type MeteringBannerType = ALERT_METERING_TYPE | null;

class MeteringStore {
  @observable.shallow
  hideAlert: HideAlert = {
    tenantAlmostOver: false,
    tenantIsOver: false,
    meAlmostOver: false,
    meIsOver: false,
  };

  @observable.shallow
  usageMap: Record<string, MeteringUsage> = {};

  @observable
  showBannerType: MeteringBannerType = null;

  @action
  setShowBannerType = (type: MeteringBannerType) => {
    this.showBannerType = type;
  };

  @action
  setHideAlert = (hideAlert: Partial<HideAlert>) => {
    this.hideAlert = mergeRight(this.hideAlert, hideAlert);
  };

  @action
  setUsage = (channelId: string, usage: MeteringUsage) => {
    this.usageMap = uiState.pickAndSetLruChannelIds(channelId, usage)(this.usageMap);
  };

  getUsage = (channelId: CHANNEL_ID) => {
    return this.usageMap[channelId];
  };

  isSetHide = (isMeChannel: boolean, bannerType: MeteringBannerType): boolean => {
    if (!bannerType) {
      return true;
    }
    const hideInfo = this.getHideInfo(isMeChannel, bannerType);
    return typeof hideInfo === 'boolean' ? hideInfo : this.isInDay(hideInfo);
  };

  public hasUsage = (channelId: CHANNEL_ID) => {
    return this.usageMap[channelId] ?? false;
  };

  public isOverChannel = (channelId: string) => {
    return this.usageMap[channelId]?.over ?? false;
  };

  private isInDay = (dateStr: string) => {
    return moment(dateStr).endOf('day').isAfter();
  };

  private getHideInfo = (isMeChannel: boolean, bannerType: MeteringBannerType): boolean | string => {
    if (isMeChannel) {
      return bannerType === ALERT_METERING_TYPE.ALMOST_OVER ? this.hideAlert.meAlmostOver : this.hideAlert.meIsOver;
    }
    return bannerType === ALERT_METERING_TYPE.ALMOST_OVER
      ? this.hideAlert.tenantAlmostOver
      : this.hideAlert.tenantIsOver;
  };

  findHidePropName = curry((isMeChannel: boolean, isAlmostOver: boolean): string => {
    if (isMeChannel) {
      return isAlmostOver ? 'meAlmostOver' : 'meIsOver';
    }
    return isAlmostOver ? 'tenantAlmostOver' : 'tenantIsOver';
  });
}

export default MeteringStore;

export const meteringStore = new MeteringStore();
